import React, { useState, useEffect } from 'react';

const SearchHeader = ({ query, setQuery, collection, setCollection }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [inputValue, setInputValue] = useState(query);
  const collections = ['HWC', 'VIP', 'CP'];

  // Sync local input with parent query (e.g. when cleared from outside)
  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const handleSearch = () => {
    setQuery(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur-md border-b border-gray-200 transition-all duration-300">
      <div className="max-w-5xl mx-auto px-4 py-3">
        <div className="flex flex-col gap-4">
          
          {/* Top Row: Brand & Search */}
          <div className="flex items-center gap-4">
            {/* Brand Logo (Icon + Text) */}
            <div className="hidden sm:flex items-center gap-2 text-blue-600 shrink-0">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
              </svg>
              <span className="font-bold text-xl tracking-tight text-gray-900">bChoir</span>
            </div>

            {/* Search Bar Container */}
            <div className="relative flex-1 group">
              <button 
                onClick={handleSearch}
                className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 group-focus-within:text-blue-500 hover:text-blue-600 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl leading-5 bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all shadow-sm"
                placeholder="Search songs, collections, or lyrics..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {/* Clear Button */}
              {inputValue && (
                <button
                  onClick={() => {
                    setInputValue('');
                    setQuery('');
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>

            {/* Filter Toggle (Mobile/Desktop) */}
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`p-2 rounded-lg border transition-colors ${
                isFilterOpen || collection
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
              aria-label="Toggle filters"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </button>
          </div>

          {/* Expandable Filter Area */}
          {(isFilterOpen || collection) && (
            <div className="flex flex-wrap items-center gap-2 pb-2 animate-slide-down">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2">
                Collection:
              </span>
              <button
                onClick={() => setCollection(null)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                  collection === null
                    ? 'bg-gray-900 text-white border-gray-900 shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                All
              </button>
              {collections.map((c) => (
                <button
                  key={c}
                  onClick={() => setCollection(c)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                    collection === c
                      ? c === 'HWC'
                        ? 'bg-[#595959] text-white border-[#595959] shadow-md transform scale-105'
                        : c === 'VIP'
                          ? 'bg-[#0022B4] text-white border-[#0022B4] shadow-md transform scale-105'
                        : c === 'CP'
                          ? 'bg-[#DA4A4A] text-white border-[#DA4A4A] shadow-md transform scale-105'
                          : 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default SearchHeader;
